import {
  SNAPSHOT_MAX_ROUNDS,
  SocialMsg,
  type ConversationReadPayload,
  type DirectMessagePayload,
  type FriendAddedPayload,
  type FriendRemovedPayload,
  type PresencePayload,
  type RequestReceivedPayload,
  type RequestResolvedPayload,
  type SearchResult,
  type SyncPayload,
} from "@clubap/shared";
import type { Room } from "colyseus.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { storedToken } from "../api.js";
import { isAuthFailure, joinSocial } from "../net.js";
import * as social from "./api.js";
import {
  EMPTY_SOCIAL,
  applySnapshot,
  conversationRead,
  directMessage,
  friendAdded,
  friendRemoved,
  mergePage,
  openingThread,
  presenceChanged,
  requestReceived,
  requestResolved,
  stableSnapshot,
  upsertFriend,
  upsertRequest,
  type SnapshotOutcome,
  type SocialState,
  type SocialTransform,
} from "./merge.js";

export type { SocialState, Thread } from "./merge.js";

/**
 * Mismo criterio que la reconexión a una sala: `tsx watch` reinicia el servidor en
 * cada guardado del editor, y ocho intentos a 1.5s cubren ese hueco con margen.
 */
const MAX_RECONNECT_ATTEMPTS = 8;
const RETRY_MS = 1500;

/** Cuánto se espera antes de reintentar una foto que no logró una ronda limpia. */
const RESTABLE_MS = 800;

/** Tope del backoff: se sigue intentando, pero un servidor caído no se martilla. */
const MAX_RECONCILE_BACKOFF_MS = 30_000;

function messageOf(cause: unknown): string {
  return cause instanceof Error ? cause.message : "Algo falló. Intenta de nuevo.";
}

export interface UseSocialOptions {
  /**
   * Cambió algo del perfil que la sala del campus dibuja (hoy, los clubes).
   *
   * La sala no se entera sola: unirse a un club pasa por HTTP y la insignia vive en
   * el estado sincronizado, así que hay que pedirle que relea.
   */
  onProfileChanged?: () => void;
  /** El token dejó de servir; la sesión completa debe volver al ingreso. */
  onSessionExpired?: () => void;
}

/**
 * El sistema social del cliente: conexión, estado y acciones.
 *
 * **Su ciclo de vida no toca el de la sala.** Depende sólo de `accountId` —nunca del
 * objeto `account`, ni de `session`, ni del id de sala—, así que cruzar una puerta,
 * ganar una moneda o reconectarse al campus no cierran el canal social. Es
 * exactamente el bug que costó 97 reconexiones en `App.tsx`, con otro efecto.
 */
export function useSocial(accountId: string | null, options: UseSocialOptions = {}) {
  const [state, setState] = useState<SocialState>(EMPTY_SOCIAL);

  /*
   * El callback de perfil, en una ref.
   *
   * Si entrara en las dependencias del efecto, cada render de `App` que lo recreara
   * reconectaría el canal social — el mismo mecanismo que rompía la sesión de sala.
   */
  const profileChangedRef = useRef(options.onProfileChanged);
  profileChangedRef.current = options.onProfileChanged;
  const sessionExpiredRef = useRef(options.onSessionExpired);
  sessionExpiredRef.current = options.onSessionExpired;

  /**
   * De quién es el estado que hay en pantalla.
   *
   * Toda respuesta HTTP en vuelo se compara contra esto ANTES y DESPUÉS del `await`:
   * al cerrar sesión o cambiar de cuenta, una petición lenta de la cuenta anterior
   * repoblaría la libreta vacía con amigos y conversaciones que ya no son de quien está
   * mirando.
   */
  const ownerRef = useRef<string | null>(accountId);

  /** El estado vigente, para los callbacks que no pueden depender de él sin recrearse. */
  const stateRef = useRef(state);
  stateRef.current = state;

  /**
   * Cuántos hechos sociales se han aplicado. No es un reloj.
   *
   * Es lo que deja saber si una foto de la base se cruzó con un aviso mientras viajaba.
   * Las horas no servirían —`Date.now()` tiene resolución de milisegundo, así que "la
   * foto ya lo traía" y "llegó después" pueden compartir marca— y mezclar avisos viejos
   * con una foto nueva la ensuciaría: una presencia retrasada volvería a apagar a
   * alguien que ya volvió.
   */
  const revisionRef = useRef(0);

  /** El reintento encadenado de la reconciliación, y cuántos seguidos van. */
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retriesRef = useRef(0);

  /**
   * Escribe sólo si el estado sigue siendo de la misma cuenta.
   *
   * La comprobación va **dentro** del actualizador y no sólo antes de encolarlo: React
   * lo ejecuta más tarde, y entre encolar y ejecutar puede haber un cierre de sesión.
   */
  const commit = useCallback((owner: string | null, updater: SocialTransform) => {
    if (ownerRef.current !== owner) return;
    setState((previous) => (ownerRef.current === owner ? updater(previous) : previous));
  }, []);

  const fail = useCallback(
    (owner: string | null, cause: unknown) => {
      commit(owner, (previous) => ({ ...previous, error: messageOf(cause) }));
    },
    [commit],
  );

  const clearError = useCallback(() => {
    setState((previous) => (previous.error === null ? previous : { ...previous, error: null }));
  }, []);

  /** Aplica un aviso y cuenta el hecho, para que una foto en vuelo sepa que se cruzó. */
  const applyEvent = useCallback(
    (owner: string | null, transform: SocialTransform) => {
      if (ownerRef.current !== owner) return;
      revisionRef.current += 1;
      setState((previous) => (ownerRef.current === owner ? transform(previous) : previous));
    },
    [],
  );

  /**
   * Pide la foto de la base hasta conseguir una **ronda limpia**.
   *
   * Se anota el contador de avisos antes de pedirla; si cambió durante el viaje, la
   * foto pudo armarse antes de ese aviso y escribirla encima lo borraría. Se descarta y
   * se vuelve a pedir. Una ronda sin avisos es autoritativa por construcción: el
   * servidor escribe en SQLite ANTES de emitir, así que todo lo que ya se aplicó en vivo
   * estaba commiteado cuando se armó la foto.
   *
   * Si NINGUNA ronda sale limpia no se aplica nada y se devuelve `dirty`. Aplicar la
   * última de todos modos sería volver a meter el bug que esto existe para evitar:
   * quedarse un momento sin la foto es inofensivo —los avisos ya se aplicaron en vivo—
   * y quien llama programa otro intento.
   */
  const reconcile = useCallback(async (): Promise<SnapshotOutcome> => {
    const owner = ownerRef.current;
    if (!owner) return "failed";

    const { outcome, snapshot } = await stableSnapshot(
      () => social.fetchSocialSnapshot(),
      () => revisionRef.current,
      SNAPSHOT_MAX_ROUNDS,
    );

    // La cuenta pudo cambiar mientras viajaba: esa foto es de otra persona.
    if (ownerRef.current !== owner || !snapshot) return outcome;

    commit(owner, (previous) => applySnapshot(previous, snapshot));
    return outcome;
  }, [commit]);

  /**
   * Reconcilia y, si no lo consiguió, **encadena** otro intento.
   *
   * Sin el encadenado una ronda sucia dejaba la libreta sin actualizar hasta la
   * siguiente acción del jugador, que puede no llegar nunca. Los intentos seguidos se
   * espacian con backoff para no martillar un servidor caído. No hay rendición
   * permanente: si el primer snapshot nunca salió limpio, la libreta no puede quedar
   * en "Cargando…" hasta que ocurra otra acción por casualidad.
   */
  const ensureRef = useRef<(() => Promise<void>) | null>(null);

  const ensureReconciled = useCallback(async () => {
    const outcome = await reconcile();

    if (outcome === "applied") {
      retriesRef.current = 0;
      return;
    }

    if (retryTimerRef.current !== null) return;

    const delay = Math.min(RESTABLE_MS * 2 ** Math.min(retriesRef.current, 6), MAX_RECONCILE_BACKOFF_MS);
    retriesRef.current += 1;
    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null;
      void ensureRef.current?.();
    }, delay);
  }, [reconcile]);

  ensureRef.current = ensureReconciled;

  useEffect(() => {
    ownerRef.current = accountId;
    /*
     * Cambiar de cuenta tira TODO, hilos incluidos.
     *
     * La foto sólo trae amigos, solicitudes y clubes, así que sin este reset una
     * conversación de la cuenta anterior sobreviviría al login siguiente.
     */
    setState(EMPTY_SOCIAL);
    /*
     * El contador SÓLO se reinicia al cambiar de cuenta, nunca al reconectar.
     *
     * Puede quedar una reconciliación de la conexión anterior en vuelo con este mismo
     * dueño: si el contador volviera a cero, esa foto vieja se vería limpia y se
     * aplicaría encima de lo que llegó después.
     */
    revisionRef.current = 0;
    retriesRef.current = 0;
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }

    if (!accountId) return;

    const owner = accountId;
    let cancelled = false;
    let room: Room<unknown> | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;

    const expireSession = () => {
      if (cancelled) return;
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      sessionExpiredRef.current?.();
    };

    const wire = (joined: Room<unknown>) => {
      const on = <T,>(type: string, toTransform: (payload: T) => SocialTransform) => {
        joined.onMessage<T>(type, (payload) => {
          if (cancelled) return;
          applyEvent(owner, toTransform(payload));
        });
      };

      on<PresencePayload>(SocialMsg.Presence, presenceChanged);
      on<RequestReceivedPayload>(SocialMsg.RequestReceived, requestReceived);
      on<RequestResolvedPayload>(SocialMsg.RequestResolved, requestResolved);
      on<FriendAddedPayload>(SocialMsg.FriendAdded, friendAdded);
      on<FriendRemovedPayload>(SocialMsg.FriendRemoved, friendRemoved);
      on<ConversationReadPayload>(SocialMsg.ConversationRead, (payload) =>
        conversationRead(payload.peerId),
      );
      on<DirectMessagePayload>(SocialMsg.DirectMessage, (payload) => directMessage(payload, owner));

      /*
       * Leerlo con la conversación delante es leerlo, también en el servidor.
       *
       * Va aparte del transform porque es un efecto, no un cambio de estado: los
       * transforms tienen que poder aplicarse sin disparar peticiones.
       */
      joined.onMessage<DirectMessagePayload>(SocialMsg.DirectMessage, (payload) => {
        if (cancelled) return;
        const message = payload.message;
        const peer = message.from === owner ? message.to : message.from;
        if (message.from !== owner && stateRef.current.activePeer === peer) {
          void social.markConversationRead(peer).catch(() => undefined);
        }
      });

      /*
       * Otra pestaña de esta cuenta cambió algo: la verdad se vuelve a pedir.
       *
       * Cuenta como aviso ANTES de pedirla: si no, una foto que ya estaba viajando se
       * vería limpia y se aplicaría encima del cambio que este mensaje anuncia.
       */
      joined.onMessage<SyncPayload>(SocialMsg.Sync, (payload) => {
        if (cancelled) return;
        revisionRef.current += 1;
        void ensureReconciled();
        if (payload?.scope === "profile") profileChangedRef.current?.();
      });

      /*
       * Los cuatro avisos de club se resuelven pidiendo la foto otra vez.
       *
       * Ninguno trae estado que aplicar directamente sobre la libreta: la solicitud
       * de ingreso la ve el presidente en el panel del club, y el anuncio vive en su
       * tablón — las dos son lecturas de UN club, no de la cuenta. Lo que sí cambia
       * en la libreta es el resumen (cuántos pendientes, si sigo dentro), y eso la
       * foto lo trae completo. Contar la revisión ANTES de pedirla es lo mismo que
       * hace `Sync`, y por la misma razón.
       */
      const clubChanged = () => {
        if (cancelled) return;
        revisionRef.current += 1;
        void ensureReconciled();
      };

      joined.onMessage(SocialMsg.ClubRequestReceived, clubChanged);
      joined.onMessage(SocialMsg.ClubRequestResolved, clubChanged);
      joined.onMessage(SocialMsg.ClubAnnouncement, clubChanged);
      /* Expulsado: además de la libreta cambia la insignia que dibuja la sala. */
      joined.onMessage(SocialMsg.ClubExpelled, () => {
        clubChanged();
        profileChangedRef.current?.();
      });

      joined.onLeave((code) => {
        if (cancelled) return;
        room = null;
        commit(owner, (previous) => ({ ...previous, connected: false }));
        if (code === 4401) {
          expireSession();
          return;
        }
        // 1000 es salida limpia (la nuestra al desmontar); de una caída se vuelve solo.
        if (code === 1000) return;
        scheduleRetry();
      });
    };

    const scheduleRetry = () => {
      if (cancelled || attempts >= MAX_RECONNECT_ATTEMPTS) return;
      attempts += 1;
      timer = setTimeout(() => void boot(), RETRY_MS);
    };

    const connect = async (): Promise<boolean> => {
      const token = storedToken();
      // Sin token no hay canal. El efecto de sesión de `App` ya manda a la pantalla
      // de ingreso; acá sólo se evita un intento que va a dar 401.
      if (!token) return false;

      try {
        const joined = await joinSocial({ token });

        if (cancelled) {
          await joined.leave();
          return false;
        }

        room = joined;
        attempts = 0;
        wire(joined);
        commit(owner, (previous) => ({ ...previous, connected: true }));
        return true;
      } catch (cause) {
        if (isAuthFailure(cause)) {
          expireSession();
          return false;
        }
        if (!cancelled) console.warn("[social] no se pudo abrir el canal", cause);
        return false;
      }
    };

    /*
     * Primero el canal, después la foto.
     *
     * En ese orden un aviso que llegue mientras se pide la foto ensucia la ronda y se
     * vuelve a pedir. Al revés quedaría un hueco que nada cubre: lo que pasara entre la
     * foto y el socket no lo contaría nadie.
     */
    const boot = async () => {
      const connected = await connect();
      if (cancelled) return;

      // `ensureReconciled` ya encadena su propio reintento si no consigue ronda limpia.
      await ensureReconciled();
      if (!cancelled && !connected) scheduleRetry();
    };

    void boot();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      void room?.leave();
      room = null;
    };
  }, [accountId, applyEvent, commit, ensureReconciled]);

  // ---------------------------------------------------------------- acciones

  /**
   * Envuelve una acción: captura la cuenta, limpia el error y reporta el fallo.
   *
   * La cuenta se comprueba **después** del `await` y antes de escribir: si entre medias
   * hubo un cierre de sesión, el resultado es de otra persona y no se aplica ni se
   * devuelve como éxito.
   */
  const act = useCallback(
    async <T,>(run: () => Promise<T>, onDone?: (owner: string, result: T) => void) => {
      const owner = ownerRef.current;
      if (!owner) return null;

      clearError();
      try {
        const result = await run();
        if (ownerRef.current !== owner) return null;

        if (onDone) {
          /*
           * Una mutación PROPIA también ensucia las fotos en vuelo.
           *
           * El contador no cuenta sólo avisos del socket: cuenta hechos aplicados. Sin
           * esto, una foto pedida antes de esta mutación podía responder después y
           * pisarla — y sin canal social no habría ningún aviso que lo delatara.
           */
          revisionRef.current += 1;
          onDone(owner, result);
        }

        return result;
      } catch (cause) {
        fail(owner, cause);
        return null;
      }
    },
    [clearError, fail],
  );

  const refresh = useCallback(async () => {
    // Con el reintento encadenado: una ronda sucia no puede dejar la libreta vieja.
    await ensureReconciled();
  }, [ensureReconciled]);

  const request = useCallback(
    (targetId: string) =>
      act(
        () => social.sendFriendRequest(targetId),
        (owner, result) =>
          commit(owner, (previous) =>
            result.friend
              ? /*
                 * Solicitud cruzada: los dos se escribieron y quedó en amistad. Hay que
                 * limpiar la suya de la bandeja aquí y no esperar al aviso, o con el
                 * socket caído seguiría pidiendo respuesta a algo ya resuelto.
                 */
                friendAdded({ friend: result.friend })(previous)
              : {
                  ...previous,
                  // Dedupe por id: el aviso de sincronización a las demás pestañas puede
                  // haber reconciliado antes de que llegue esta respuesta.
                  outgoing: result.request
                    ? upsertRequest(previous.outgoing, result.request)
                    : previous.outgoing,
                },
          ),
      ),
    [act, commit],
  );

  /* La misma transformación que el aviso: limpia las solicitudes de esa persona y
     reordena la lista. Depender del socket para eso lo rompería estando caído. */
  const accept = useCallback(
    (requestId: string) =>
      act(
        () => social.acceptFriendRequest(requestId),
        (owner, friend) => commit(owner, friendAdded({ friend })),
      ),
    [act, commit],
  );

  const reject = useCallback(
    (requestId: string) =>
      act(
        () => social.rejectFriendRequest(requestId),
        (owner) =>
          commit(owner, (previous) => ({
            ...previous,
            incoming: previous.incoming.filter((entry) => entry.id !== requestId),
          })),
      ),
    [act, commit],
  );

  const cancel = useCallback(
    (requestId: string) =>
      act(
        () => social.cancelFriendRequest(requestId),
        (owner) =>
          commit(owner, (previous) => ({
            ...previous,
            outgoing: previous.outgoing.filter((entry) => entry.id !== requestId),
          })),
      ),
    [act, commit],
  );

  const unfriend = useCallback(
    (friendId: string) =>
      act(
        () => social.removeFriend(friendId),
        (owner) => commit(owner, friendRemoved({ accountId: friendId })),
      ),
    [act, commit],
  );

  const search = useCallback(
    async (query: string): Promise<SearchResult[]> => (await act(() => social.searchPlayers(query))) ?? [],
    [act],
  );

  const openThread = useCallback(
    async (friendId: string) => {
      const owner = ownerRef.current;
      if (!owner) return;

      clearError();
      // El hilo se crea ANTES de pedir la página: un mensaje que llegue mientras viaja
      // necesita dónde caer, o desaparecería de la pantalla al escribir la respuesta.
      commit(owner, (previous) => openingThread(previous, friendId));

      try {
        const page = await social.fetchConversation(friendId);
        if (ownerRef.current !== owner) return;

        commit(owner, (previous) => ({
          ...previous,
          threads: { ...previous.threads, [friendId]: mergePage(previous.threads[friendId], page) },
        }));

        /*
         * El contador baja cuando el SERVIDOR lo confirma, no antes.
         *
         * Ponerlo a cero al abrir era mentir en cuanto la petición fallara: la libreta
         * decía "leído" y la base seguía contándolos, así que reaparecían solos en la
         * siguiente reconciliación sin que nadie entendiera por qué.
         */
        await social.markConversationRead(friendId);
        if (ownerRef.current !== owner) return;

        revisionRef.current += 1;
        commit(owner, conversationRead(friendId));
      } catch (cause) {
        if (ownerRef.current !== owner) return;
        commit(owner, (previous) => {
          const thread = previous.threads[friendId];
          if (!thread) return previous;
          return { ...previous, threads: { ...previous.threads, [friendId]: { ...thread, loading: false } } };
        });
        fail(owner, cause);
      }
    },
    [clearError, commit, fail],
  );

  const closeThread = useCallback(() => {
    setState((previous) => ({ ...previous, activePeer: null }));
  }, []);

  const loadOlder = useCallback(
    async (friendId: string) => {
      const owner = ownerRef.current;
      const thread = stateRef.current.threads[friendId];
      if (!owner || !thread?.nextCursor || thread.loading) return;

      const cursor = thread.nextCursor;

      const setLoading = (loading: boolean) =>
        commit(owner, (previous) => {
          const current = previous.threads[friendId];
          if (!current) return previous;
          return { ...previous, threads: { ...previous.threads, [friendId]: { ...current, loading } } };
        });

      setLoading(true);

      try {
        const page = await social.fetchConversation(friendId, cursor);
        if (ownerRef.current !== owner) return;

        // Mezcla, no reemplazo, y con techo duro: lo que llegó en vivo mientras la
        // página viajaba tiene que seguir ahí.
        commit(owner, (previous) => ({
          ...previous,
          threads: { ...previous.threads, [friendId]: mergePage(previous.threads[friendId], page) },
        }));
      } catch (cause) {
        if (ownerRef.current !== owner) return;
        setLoading(false);
        fail(owner, cause);
      }
    },
    [commit, fail],
  );

  /*
   * Lo propio se aplica con la MISMA transformación que un mensaje entrante.
   *
   * Al ser mío no sube ningún contador, y a cambio se gana lo demás gratis: guarda en el
   * hilo, deduplica contra el aviso que llegará por el canal, y reordena la lista para
   * que la conversación suba.
   */
  const send = useCallback(
    (friendId: string, text: string) =>
      act(
        () => social.sendDirectMessage(friendId, text),
        (owner, message) => commit(owner, directMessage({ message }, owner)),
      ),
    [act, commit],
  );

  /*
   * Entrar y salir de un club se aplican con la RESPUESTA HTTP, no con el aviso.
   *
   * El aviso de sincronización pone al día a las demás pestañas, pero no se puede
   * depender de él acá: con el canal social caído la petición saldría bien y la pantalla
   * —y la insignia de la sala— se quedarían como estaban.
   */
  const applyClubChange = useCallback(
    (owner: string, change: { clubs: SocialState["clubs"] }) => {
      commit(owner, (previous) => ({ ...previous, clubs: change.clubs }));
      profileChangedRef.current?.();
    },
    [commit],
  );

  const joinClub = useCallback(
    (clubId: string) => act(() => social.joinClub(clubId), applyClubChange),
    [act, applyClubChange],
  );

  const leaveClub = useCallback(
    (clubId: string) => act(() => social.leaveClub(clubId), applyClubChange),
    [act, applyClubChange],
  );

  const roster = useCallback((clubId: string) => act(() => social.fetchClubRoster(clubId)), [act]);

  /*
   * Nombrar presidente. Devuelve el roster ya releído por el servidor.
   *
   * El resumen de clubes se aplica con `applyClubChange` como unirse y salir, pero el
   * roster NO se guarda en el estado del hook: es una lectura puntual que vive en el
   * panel, y meterla acá obligaría a invalidarla desde cada aviso social.
   */
  /*
   * Moderación y tablón. Ninguna de estas guarda su resultado en el estado del hook.
   *
   * Las solicitudes de un club y sus anuncios son lecturas de UN club concreto, y el
   * estado social es de la cuenta entera: meterlas acá obligaría a invalidarlas desde
   * cada aviso que llegue por cualquier otro club. Viven en el panel, que es quien
   * sabe qué club tienes abierto. Lo que sí se aplica es el resumen de clubes, porque
   * ése sí es de la cuenta.
   */
  const clubRequests = useCallback(
    (clubId: string) => act(() => social.fetchClubRequests(clubId)),
    [act],
  );

  const resolveClubRequest = useCallback(
    (clubId: string, requestId: string, outcome: "accept" | "reject") =>
      act(
        () => social.resolveClubRequest(clubId, requestId, outcome),
        (owner, result) => applyClubChange(owner, { clubs: result.clubs }),
      ),
    [act, applyClubChange],
  );

  const cancelClubRequest = useCallback(
    (clubId: string) => act(() => social.cancelClubRequest(clubId), applyClubChange),
    [act, applyClubChange],
  );

  const expel = useCallback(
    (clubId: string, playerId: string) =>
      act(
        () => social.expelMember(clubId, playerId),
        (owner, result) => applyClubChange(owner, { clubs: result.clubs }),
      ),
    [act, applyClubChange],
  );

  const setBlurb = useCallback(
    (clubId: string, text: string) =>
      act(
        () => social.setClubBlurb(clubId, text),
        (owner, result) => applyClubChange(owner, { clubs: result.clubs }),
      ),
    [act, applyClubChange],
  );

  const announcements = useCallback(
    (clubId: string) => act(() => social.fetchAnnouncements(clubId)),
    [act],
  );

  const announce = useCallback(
    (clubId: string, text: string) => act(() => social.announce(clubId, text)),
    [act],
  );

  const setPresident = useCallback(
    (clubId: string, playerId: string | null) =>
      act(
        () => social.setClubPresident(clubId, playerId),
        (owner, result) => applyClubChange(owner, { clubs: result.clubs }),
      ),
    [act, applyClubChange],
  );

  /*
   * El paquete de acciones, memorizado.
   *
   * Sin esto sería un objeto nuevo en cada render, y los efectos del panel que lo
   * llevan en sus dependencias —el de Escape, por ejemplo— se resuscribirían con cada
   * mensaje que llega. Todas las acciones ya son estables por su cuenta.
   */
  const actions = useMemo(
    () => ({
      refresh,
      request,
      accept,
      reject,
      cancel,
      unfriend,
      search,
      openThread,
      closeThread,
      loadOlder,
      send,
      joinClub,
      leaveClub,
      roster,
      clubRequests,
      resolveClubRequest,
      cancelClubRequest,
      expel,
      setBlurb,
      announcements,
      announce,
      setPresident,
      clearError,
    }),
    [
      refresh,
      request,
      accept,
      reject,
      cancel,
      unfriend,
      search,
      openThread,
      closeThread,
      loadOlder,
      send,
      joinClub,
      leaveClub,
      roster,
      clubRequests,
      resolveClubRequest,
      cancelClubRequest,
      expel,
      setBlurb,
      announcements,
      announce,
      setPresident,
      clearError,
    ],
  );

  return { state, actions };
}

export type SocialActions = ReturnType<typeof useSocial>["actions"];
